import React from 'react';
import { Eye, Edit2, Trash2, Printer, FileSearch, Calendar, User, Package, Box } from 'lucide-react';
import { List, type RowComponentProps } from 'react-window';
import { Order } from '@/types';

const API_BASE_URL = (window as any).ENV?.VITE_API_URL || import.meta.env?.VITE_API_URL || 'http://localhost:5000';

/** Abre el HTML de impresión en nueva pestaña con diálogo de imprimir/guardar PDF */
const openPrintPreview = (orderId: string) => {
  window.open(`${API_BASE_URL}/api/orders/${orderId}/pdf?print=true`, '_blank');
};

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OrdersTableViewProps {
  orders: Order[];
  onEdit: (orderId: string) => void;
  onDelete: (orderId: string) => void;
  onViewDetails: (order: Order) => void;
  onPrint: (orderId: string) => void;
  statusConfig: Record<string, { label: string; color: string; icon: any }>;
  canEdit: boolean;
  canDelete: boolean;
}

// Layout columnas tabla desktop: Fecha | Cliente | Creado por | Estado | Productos | Cajas | Ruta | Acciones
const GRID_COLS = '80px minmax(180px, 1.4fr) 130px 130px 90px 80px minmax(140px, 1fr) 280px';
const ROW_HEIGHT_DESKTOP = 52;
const ROW_HEIGHT_MOBILE = 305;
const MAX_LIST_HEIGHT = 720;

// --- Desktop row (memoizada) ---
type DesktopRowProps = {
  orders: Order[];
  statusConfig: OrdersTableViewProps['statusConfig'];
  canEdit: boolean;
  canDelete: boolean;
  onEdit: OrdersTableViewProps['onEdit'];
  onDelete: OrdersTableViewProps['onDelete'];
  onViewDetails: OrdersTableViewProps['onViewDetails'];
  onPrint: OrdersTableViewProps['onPrint'];
};

const DesktopRow = ({
  index,
  style,
  orders,
  statusConfig,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onViewDetails,
  onPrint,
}: RowComponentProps<DesktopRowProps>) => {
  const order = orders[index];
  const status = statusConfig[order.status];
  const StatusIcon = status.icon;
  const totalBoxes = React.useMemo(
    () => order.lines.filter(l => l.unit === 'cajas').reduce((acc, l) => acc + l.quantity, 0),
    [order.lines]
  );
  const dateLabel = React.useMemo(
    () => new Date(order.orderDate).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
    [order.orderDate]
  );

  return (
    <div
      style={{ ...style, display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
      className="border-b border-slate-100 px-3 hover:bg-slate-50/60 text-sm"
    >
      <div className="font-medium text-slate-700">{dateLabel}</div>
      <div className="font-bold truncate" title={order.customerName}>{order.customerName}</div>
      <div className="truncate text-slate-600">{order.createdBy?.name || 'Desconocido'}</div>
      <div>
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${status.color}`}>
          <StatusIcon size={12} />
          {status.label}
        </span>
      </div>
      <div className="text-slate-700">{order.lines.length}</div>
      <div>
        <span className={totalBoxes > 0 ? "font-bold text-slate-900" : "text-slate-400"}>
          {totalBoxes}
        </span>
      </div>
      <div className="truncate text-xs text-slate-600">
        {order.routeName ? order.routeName : <span className="text-slate-400">-</span>}
      </div>
      <div className="flex justify-end gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onViewDetails(order)}>
              <Eye size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Ver detalles</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:bg-slate-50" onClick={() => openPrintPreview(order.id)}>
              <FileSearch size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Previsualizar impresion</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => onPrint(order.id)}>
              <Printer size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Imprimir</TooltipContent>
        </Tooltip>

        {canEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => onEdit(order.id)}>
                <Edit2 size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Editar</TooltipContent>
          </Tooltip>
        )}

        {canDelete && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onDelete(order.id)}>
                <Trash2 size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Eliminar</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

// --- Mobile row (memoizada) ---
const MobileRow = ({
  index,
  style,
  orders,
  statusConfig,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onViewDetails,
  onPrint,
}: RowComponentProps<DesktopRowProps>) => {
  const order = orders[index];
  const status = statusConfig[order.status];
  const StatusIcon = status.icon;
  const totalBoxes = React.useMemo(
    () => order.lines.filter(l => l.unit === 'cajas').reduce((acc, l) => acc + l.quantity, 0),
    [order.lines]
  );
  const dateLabel = React.useMemo(
    () => new Date(order.orderDate).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    [order.orderDate]
  );

  return (
    <div style={style} className="px-1 pb-3">
      <Card className="border-l-4 border-l-blue-400">
        <CardContent className="p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm">
            <Calendar size={14} className="text-slate-400 shrink-0" />
            <span className="font-medium text-slate-500">Fecha:</span>
            <span className="font-semibold text-slate-900">{dateLabel}</span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <User size={14} className="text-slate-400 shrink-0" />
            <span className="font-medium text-slate-500">Cliente:</span>
            <span className="font-bold text-slate-900 truncate">{order.customerName}</span>
          </div>

          {order.routeName && (
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-slate-500">Ruta:</span>
              <span className="text-slate-700 truncate">{order.routeName}</span>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm">
            <User size={14} className="text-slate-400 shrink-0" />
            <span className="font-medium text-slate-500">Creado por:</span>
            <span className="text-slate-700 truncate">{order.createdBy?.name || 'Desconocido'}</span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-slate-500">Estado:</span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${status.color}`}>
              <StatusIcon size={12} />
              {status.label}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Package size={14} className="text-slate-400 shrink-0" />
            <span className="font-medium text-slate-500">Productos:</span>
            <span className="font-semibold text-slate-900">{order.lines.length}</span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Box size={14} className="text-slate-400 shrink-0" />
            <span className="font-medium text-slate-500">Cajas:</span>
            <span className={totalBoxes > 0 ? "font-bold text-slate-900" : "text-slate-400"}>
              {totalBoxes}
            </span>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-100 mt-1">
            <span className="font-medium text-slate-500 text-sm mr-auto">Acciones:</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onViewDetails(order)}>
                <Eye size={16} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:bg-slate-50" onClick={() => openPrintPreview(order.id)}>
                <FileSearch size={16} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => onPrint(order.id)}>
                <Printer size={16} />
              </Button>
              {canEdit && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => onEdit(order.id)}>
                  <Edit2 size={16} />
                </Button>
              )}
              {canDelete && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onDelete(order.id)}>
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tailwind `xl` breakpoint = 1280px. Detectamos por JS para renderizar SÓLO
// una vista a la vez (evita montar las dos List virtualizadas en paralelo).
const DESKTOP_MQ = '(min-width: 1280px)';

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia(DESKTOP_MQ).matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(DESKTOP_MQ);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isDesktop;
};

export const OrdersTableView: React.FC<OrdersTableViewProps> = ({
  orders,
  onEdit,
  onDelete,
  onViewDetails,
  onPrint,
  statusConfig,
  canEdit,
  canDelete,
}) => {
  const isDesktop = useIsDesktop();
  const rowHeight = isDesktop ? ROW_HEIGHT_DESKTOP : ROW_HEIGHT_MOBILE;
  const listHeight = Math.min(MAX_LIST_HEIGHT, Math.max(rowHeight, orders.length * rowHeight));

  const rowProps = React.useMemo(
    () => ({
      orders,
      statusConfig,
      canEdit,
      canDelete,
      onEdit,
      onDelete,
      onViewDetails,
      onPrint,
    }),
    [orders, statusConfig, canEdit, canDelete, onEdit, onDelete, onViewDetails, onPrint]
  );

  if (orders.length === 0) {
    return (
      <div className="p-4">
        <div className="py-8 text-center text-sm text-slate-500">No hay comandas</div>
      </div>
    );
  }

  if (isDesktop) {
    return (
      <div className="p-4">
        <div className="overflow-hidden rounded-md border border-slate-200">
          <div
            style={{ display: 'grid', gridTemplateColumns: GRID_COLS }}
            className="bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200"
          >
            <div>Fecha</div>
            <div>Cliente</div>
            <div>Creado por</div>
            <div>Estado</div>
            <div>Productos</div>
            <div>Cajas</div>
            <div>Ruta</div>
            <div className="text-right">Acciones</div>
          </div>
          <List
            rowCount={orders.length}
            rowHeight={ROW_HEIGHT_DESKTOP}
            defaultHeight={listHeight}
            rowComponent={DesktopRow}
            rowProps={rowProps}
            overscanCount={6}
            style={{ height: listHeight }}
          />
        </div>
      </div>
    );
  }

  // Mobile/tablet — padding-right en el List para que el scrollbar no
  // se superponga visualmente sobre el contenido de las cards.
  return (
    <div className="p-4">
      <List
        rowCount={orders.length}
        rowHeight={ROW_HEIGHT_MOBILE}
        defaultHeight={listHeight}
        rowComponent={MobileRow}
        rowProps={rowProps}
        overscanCount={3}
        style={{ height: listHeight, paddingRight: 12 }}
      />
    </div>
  );
};
